// Free resources (/free-resources), as FreeResources.dc.html draws it: every free item the member
// can see, each card from its own catalogue row (settings.card), and a line on how each one opens.
// - The Next Deal Scorecard: a plain link to scorecard.adamslee.co.uk in a new tab, with no name
//   or email in the address: ScoreApp and MailerLite look after the scorecard, and the hub only
//   links to it (Adam, 5 Oct), which also keeps members' emails away from the page's tracking. The
//   hub records that they opened it.
// - The 21 Ways guide: a link sign-file signs for 2 minutes, opened at once, so the PDF downloads.
//   Nothing is emailed after it. A free pack of 2 files or more lists each file under its blurb,
//   each with its own Download, the same way (the product-types plan, 13 Oct).
// - The Restart Challenge: 1 click, through restart-join, which posts their first name and email
//   from the database to the challenge's own route. Once in, the card says "You're in", and on a
//   later visit when they joined. It claims nothing about the challenge's emails, which are Adam's
//   MailerLite automation (Adam, 5 Oct).
// Staff viewing as a member see the member's page, with Download and Join off.
import { useCallback, useEffect, useState } from 'react'
import { useMember } from '../app/member.jsx'
import { useNow } from '../lib/clock.js'
import { useViewingAs, readOnlyControl, VIEWING_AS_LINE } from '../lib/viewAs.jsx'
import { HubFunctionError } from '../lib/functions.js'
import { FreePage, PageHead, LoadFailed } from '../free/FreePage.jsx'
import { freeItems, infoText, joinedLine, webinarsProduct } from '../free/resources.js'
import { loadJoins, recordOpened, signFile, joinRestart, loadSavedStrategy } from '../free/load.js'
import { ENV } from '../lib/env.js'
import { navigate } from '../lib/router.jsx'
import { REPORT_PATH, REPORT_TITLE, strategyOpen } from '../strategy/route.js'
import { savedGuide } from '../strategy/worksheets.mjs'
import { BarsMark, DownloadIcon, InfoIcon, OutArrow, TickIcon } from '../free/Icons.jsx'
import { useCovers, coverState } from '../free/covers.js'

// A refusal's own line (the functions write them), or the shared one when there was no answer to read
const lineFor = e => (e instanceof HubFunctionError ? e.message : 'Something went wrong. Try again.')

export default function FreeResources() {
  const { me, lib, refreshLibrary } = useMember()
  const personId = me.person.id
  const [joins, setJoins] = useState({ status: 'loading', rows: [] })

  const load = useCallback(() => {
    setJoins({ status: 'loading', rows: [] })
    loadJoins(personId)
      .then(rows => setJoins({ status: 'ready', rows }))
      .catch(() => setJoins({ status: 'error', rows: [] }))
  }, [personId])
  useEffect(() => { load() }, [load])

  // The way back to the property strategy report (Stage 2, S2.8): 1 line, only for an account with a saved report,
  // and under it their starter guide, the version the report's button opens (worksheets.mjs savedGuide), in a new tab;
  // a failed read shows nothing
  const [report, setReport] = useState(null)
  useEffect(() => {
    if (!strategyOpen(ENV.context)) return undefined
    let live = true
    loadSavedStrategy(personId).then(row => { if (live) setReport(row ? { guide: savedGuide(row) } : null) }).catch(() => {})
    return () => { live = false }
  }, [personId])

  const retry = () => {
    if (lib.status === 'error') refreshLibrary()
    if (joins.status === 'error') load()
  }

  const failed = lib.status === 'error' || joins.status === 'error'
  const ready = lib.data && joins.status === 'ready'
  const items = ready ? freeItems(lib.data) : []
  const webinars = ready ? webinarsProduct(lib.data) : null
  const covers = useCovers(items)

  return (
    <FreePage title="Free resources">
      <PageHead
        heading="Free resources"
        lede="Anything free Adam adds shows up here, for everyone with an account."
        other={webinars ? { to: '/free-webinars', label: 'Free webinars' } : null}
      />
      {report && (
        <p className="fr-strategy">
          <a href={REPORT_PATH} onClick={e => { e.preventDefault(); navigate(REPORT_PATH) }}>{REPORT_TITLE} <span aria-hidden="true">→</span></a>
        </p>
      )}
      {report?.guide && (
        <p className="fr-strategy-guide">
          <a href={report.guide.href} target="_blank" rel="noopener noreferrer">{report.guide.name} <span aria-hidden="true">→</span></a>
        </p>
      )}
      {failed && <LoadFailed onRetry={retry} />}
      {!failed && !ready && <div className="fp-loading" aria-busy="true" />}
      {ready && items.length === 0 && <p className="fp-empty">Nothing free here at the moment.</p>}
      {ready && items.length > 0 && (
        <>
          <section className="fr-grid" aria-label="Free resources">
            {items.map(item => (
              <FreeCard key={item.id} item={item} covers={covers} joins={joins.rows} email={me.person.email} personId={personId} />
            ))}
          </section>
          <section className="fr-info">
            <InfoIcon />
            <span className="fr-info-text">{infoText(items)}</span>
          </section>
        </>
      )}
    </FreePage>
  )
}

function FreeCard({ item, covers, joins, email, personId }) {
  return (
    <article className="fr-card">
      <Art item={item} covers={covers} />
      <div className="fr-body">
        <span className="fr-kind">{item.kindLabel}</span>
        <h2 className="fr-h2">{item.name}</h2>
        <p className="fr-blurb">{item.blurb}</p>
        {item.action.kind === 'download' && item.action.files && (
          <ul className="fr-files" aria-label={`${item.name}: its files`}>
            {item.action.files.map(file => (
              <li key={file.placementId} className="fr-file">
                <span className="fr-file-title">{file.title}</span>
                <DownloadAction item={item} placementId={file.placementId} label="Download" fileTitle={file.title} small />
              </li>
            ))}
          </ul>
        )}
        <div className="fr-foot">
          {item.action.kind === 'link' && <ScorecardAction item={item} personId={personId} />}
          {item.action.kind === 'download' && !item.action.files && <DownloadAction item={item} />}
          {item.action.kind === 'download' && item.action.files && item.action.note && <span className="fr-note">{item.action.note}</span>}
          {item.action.kind === 'join' && <JoinAction item={item} email={email} joined={joins.find(j => j.product_id === item.id)} />}
        </div>
      </div>
    </article>
  )
}

// Each item's art in its own look, as the board draws 3: the Accelerator's light (a mark, then a line
// and the name), the Accelerator's solid (a line, then the name) and the master brand's light (a
// gold bar, then a large line and the name). An item in a look or tone no board draws takes the
// master brand's light, the shell's own. An item with its own cover photo (3 Nov) shows the photo
// filling the top of the card instead, empty while its link is on its way, and its designed art if the
// photo can't be had.
function Art({ item, covers }) {
  const photo = coverState(covers, item)
  if (photo) {
    return (
      <div className="fr-art fr-art--cover" aria-hidden="true">
        {photo.url && <img key={photo.url} className="fr-cover-img" src={photo.url} alt="" decoding="async"
          onError={e => covers.failed(item, e.currentTarget.src)} onLoad={e => covers.loaded(item, e.currentTarget.src)} />}
      </div>
    )
  }
  if (item.art === 'acc-light') {
    return (
      <div className="fr-art fr-art--acc-light" aria-hidden="true">
        <BarsMark />
        <div className="fr-art-words">
          <span className="fr-art-line">{item.artLine}</span>
          <span className="fr-art-headline">{item.artHeadline}</span>
        </div>
      </div>
    )
  }
  if (item.art === 'acc-solid') {
    return (
      <div className="fr-art fr-art--acc-solid" aria-hidden="true">
        <span className="fr-art-line">{item.artLine}</span>
        <span className="fr-art-headline">{item.artHeadline}</span>
      </div>
    )
  }
  return (
    <div className="fr-art fr-art--master-light" aria-hidden="true">
      <span className="fr-art-bar" />
      <div className="fr-art-words">
        <span className="fr-art-line">{item.artLine}</span>
        <span className="fr-art-headline">{item.artHeadline}</span>
      </div>
    </div>
  )
}

// A real link in a new tab, so no pop-up blocker stops it; the hub notes the open without waiting
function ScorecardAction({ item, personId }) {
  const viewingAs = useViewingAs()
  function opened() {
    if (viewingAs) return
    recordOpened(personId, item.id).catch(() => { /* the tab opens whatever happens here */ })
  }
  return (
    <>
      <a className="fr-btn" href={item.action.url} target="_blank" rel="noopener noreferrer" onClick={opened}>
        {item.action.label}<OutArrow />
      </a>
      <span className="fr-note">{item.action.note}</span>
    </>
  )
}

// The guide: signed, then opened in this tab, where the file downloads under its own name. In a pack of
// 2 files or more, 1 file's own Download, named for the file.
function DownloadAction({ item, placementId = item.action.placementId, label = item.action.label, fileTitle = null, small = false }) {
  const viewingAs = useViewingAs()
  const [state, setState] = useState({ status: 'idle', line: null })
  const busy = state.status === 'busy'
  const done = state.status === 'done'

  function download() {
    if (busy || viewingAs) return
    setState({ status: 'busy', line: null })
    signFile(placementId)
      .then(data => {
        if (typeof data?.url !== 'string' || !data.url.startsWith('https://')) throw new Error('no link')
        window.location.assign(data.url)
        setState({ status: 'done', line: null })
      })
      .catch(e => setState({ status: 'failed', line: lineFor(e) }))
  }

  const note = viewingAs ? VIEWING_AS_LINE : state.line || item.action.note
  const cls = `${done ? 'fr-btn fr-btn--done' : 'fr-btn'}${small ? ' fr-btn--file' : ''}`
  return (
    <>
      <button type="button" className={cls} onClick={download} aria-label={fileTitle ? `${done ? 'Downloaded' : label}: ${fileTitle}` : undefined}
        {...readOnlyControl(viewingAs)} aria-busy={busy || undefined}>
        <DownloadIcon />{done ? 'Downloaded' : label}
      </button>
      {small
        ? (state.line || viewingAs) && <span className="fr-note fr-file-note" role={state.line ? 'alert' : undefined}>{viewingAs ? VIEWING_AS_LINE : state.line}</span>
        : <span className="fr-note" role={state.line ? 'alert' : undefined}>{note}</span>}
    </>
  )
}

// The Restart Challenge: Join, then the joined box
function JoinAction({ item, email, joined }) {
  const viewingAs = useViewingAs()
  const nowMs = useNow()
  const [state, setState] = useState({ status: 'idle', line: null, joinedAt: null, justNow: false })
  const busy = state.status === 'busy'
  const joinedAt = state.joinedAt || joined?.occurred_at || null

  function join() {
    if (busy || viewingAs) return
    setState({ status: 'busy', line: null, joinedAt: null, justNow: false })
    joinRestart()
      .then(data => {
        if (!data?.ok) throw new Error('not joined')
        setState({ status: 'joined', line: null, joinedAt: data.joined_at || new Date(nowMs).toISOString(), justNow: !data.already })
      })
      .catch(e => setState({ status: 'failed', line: lineFor(e), joinedAt: null, justNow: false }))
  }

  if (joinedAt) {
    const line = joinedLine({ joinedAt, justNow: state.justNow })
    return (
      <div className="fr-joined" role="status">
        <span className="fr-joined-head"><TickIcon />You're in</span>
        {line && <span className="fr-joined-line">{line}</span>}
      </div>
    )
  }
  const note = viewingAs ? VIEWING_AS_LINE : state.line || [`Joins with ${email}.`, item.action.note].filter(Boolean).join(' ')
  return (
    <>
      <button type="button" className="fr-btn" onClick={join} {...readOnlyControl(viewingAs)} aria-busy={busy || undefined}>{item.action.label}</button>
      <span className="fr-note" role={state.line ? 'alert' : undefined}>{note}</span>
    </>
  )
}
